import classNames from 'classnames';
import { AnimatePresence, m } from 'framer-motion';
import { useMemo, useState } from 'react';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList, {
  CtaLinkListProps,
} from '@/components/organisms/cta-link-list/cta-link-list';
import TabMenu from '@/components/organisms/tab-menu/tab-menu';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './stack-and-tabs.module.scss';

interface Section {
  ctaLinks: CtaLinkListProps['items'];
  headline: string;
  id: string;
  image?: Content.Properties.Image;
  sectionName: string;
  subHeadline: string;
  text: string;
}

export interface StackAndTabsProps extends ModuleWrapperProps {
  headline: string;
  layout: 'stacked' | 'tabs';
  sections: Section[];
  text: string;
}

const StackAndTabs = ({
  headline,
  layout,
  sections = [],
  text,
  ...rest
}: StackAndTabsProps) => {
  const isLaptop = useWindowSize('laptop');
  const [activeSection, setActiveSection] = useState<Section>(sections[0]);
  const isTabbed = layout === 'tabs';

  const renderNavigation = useMemo(() => {
    const isStackMenu = !isTabbed && isLaptop;

    if (isStackMenu) {
      return (
        <div className={styles.navigation}>
          {sections.map((section, sectionIndex) => (
            <button
              key={sectionIndex}
              className={classNames(styles.button, {
                [styles.isActive]: section.id === activeSection.id,
              })}
              onClick={() => setActiveSection(section)}
            >
              <Typography className={styles.label} tagStyle="headingSmall">
                {section.sectionName}
              </Typography>
            </button>
          ))}
        </div>
      );
    }

    return (
      <TabMenu
        activeItem={{
          type: 'id',
          value: activeSection.id,
        }}
        className={styles.menu}
        tabs={sections.map(section => ({
          id: section.id,
          onClick: () => {
            setActiveSection(section);
          },
          text: section.sectionName,
        }))}
      />
    );
  }, [activeSection.id, isLaptop, isTabbed, sections]);

  return (
    <ModuleWrapper
      {...rest}
      className={classNames(styles.component, {
        [styles.isTabbed]: isTabbed,
      })}
    >
      <Container>
        <Row className={styles.row}>
          <Column
            className={styles.column}
            width={{ laptop: !isTabbed ? 5 : undefined }}
          >
            <div className={styles.headlineWrapper}>
              <Typography
                className={styles.headline}
                tag="h2"
                tagStyle="displayMedium"
              >
                {headline}
              </Typography>

              <RichText className={styles.text} text={text} />
            </div>

            {renderNavigation}
          </Column>

          <Column
            offset={{ laptop: !isTabbed ? 1 : undefined }}
            width={{ laptop: !isTabbed ? 5 : undefined }}
          >
            <AnimatePresence initial={false} mode="wait">
              {sections.map(section => {
                const isStacked = !isTabbed && isLaptop;

                return (
                  <m.div
                    key={`${isLaptop ? 'large' : 'small'}-${section.id}`}
                    animate={
                      activeSection.id === section.id
                        ? {
                            display: 'flex',
                            opacity: 1,
                            transition: { delay: 0.25, duration: 0.25 },
                            x: 0,
                            y: 0,
                          }
                        : {
                            display: 'none',
                            opacity: 0,
                            x: isStacked ? -72 : 0,
                            y: isStacked ? 0 : -24,
                          }
                    }
                    className={styles.info}
                    transition={{ duration: 0.25 }}
                  >
                    {section.image && (
                      <div className={styles.image}>
                        <Image
                          alt={section.image.altText}
                          aspectRatio="sixteenByNine"
                          fill
                          src={section.image.url}
                          srcSet={{
                            laptop: {
                              width: isTabbed ? 660 : 550,
                            },
                            tablet: {
                              width: 550,
                            },
                          }}
                          unoptimized
                          width={340}
                        />
                      </div>
                    )}

                    <div className={styles.texts}>
                      <div className={styles.headlines}>
                        <Typography
                          className={styles.headline}
                          tag="h3"
                          tagStyle="headingMedium"
                        >
                          {section.headline}
                        </Typography>

                        <Typography
                          className={styles.headline}
                          tag="h4"
                          tagStyle="bodyLargeBold"
                        >
                          {section.subHeadline}
                        </Typography>
                      </div>

                      <RichText text={section.text} />

                      <CtaLinkList items={section.ctaLinks} />
                    </div>
                  </m.div>
                );
              })}
            </AnimatePresence>
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

StackAndTabs.displayName = 'StackAndTabs';

export default StackAndTabs;
